import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Heart, Award, Globe, Users, TrendingUp, Code, Zap, CheckCircle, ArrowRight, Sparkles, Clock, Building2, Star, X, Briefcase } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import earlitImg from '../data/pics/aboutus/earlit.png';
import glennceoImg from '../data/pics/aboutus/glennceo.png';
import lawropsImg from '../data/pics/aboutus/lawrops.jpeg';

const About = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({});

  const heroSlides = [
    {
      title: "Your Trusted IT Infrastructure Partner",
      subtitle: "Delivering enterprise-grade IT solutions with precision and reliability since 2026",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2070",
      highlight: "Your Strategic Partner"
    },
    {
      title: "More Than Just IT Services",
      subtitle: "Delivering talent, reliability, and ongoing support you can count on",
      image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=2069",
      highlight: "Industry Experts"
    },
    {
      title: "Seasoned professionals delivering results you can rely on.",
      subtitle: "Where experience meets precision — and every project is handled with confidence",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070",
      highlight: "Trusted Worldwide"
    }
  ];

  const stats = [
    { icon: Briefcase, value: 500, suffix: '+', label: 'Projects Completed', description: 'Successful implementations', color: 'from-blue-500 to-cyan-500' },
    { icon: Building2, value: 150, suffix: '+', label: 'Enterprise Clients', description: 'Long-term partnerships', color: 'from-purple-500 to-pink-500' },
    { icon: TrendingUp, value: 98, suffix: '%', label: 'Client Satisfaction', description: 'Happy customers', color: 'from-green-500 to-emerald-500' },
    { icon: Award, value: 15, suffix: '+', label: 'Years Experience', description: 'Deep industry expertise', color: 'from-orange-500 to-yellow-500' },
  ];

  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We deliver every project to the highest standard — from detailed planning to final handover, quality is never compromised.',
      color: 'from-blue-500 to-cyan-500',
      benefits: ['Rigorous quality checks', 'Certified engineers', 'Continuous improvement']
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'Transparency and honesty guide every client interaction — we provide clear scopes, honest timelines, and accurate pricing.',
      color: 'from-red-500 to-pink-500',
      benefits: ['Honest communication', 'Ethical practices', 'Long-term relationships']
    },
    {
      icon: Award,
      title: 'Clients come first',
      description: 'We listen closely, respond quickly, and shape our services around what matters most to you. Your priorities become our priorities.',
      color: 'from-purple-500 to-indigo-500',
      benefits: ['Client-Centered Approach', 'Client-Let Solutions', 'Client-Focused Service']
    },
    {
      icon: Globe,
      title: 'Partnership',
      description: 'We build lasting partnerships with our clients, providing ongoing support and guidance long after project completion.',
      color: 'from-green-500 to-emerald-500',
      benefits: ['Post-project support', 'Dedicated account managers', 'Long-term maintenance']
    },
  ];

  const timeline = [
    {
      year: '2020',
      title: 'Foundation',
      description: 'Tasks IT Services was established to deliver enterprise-grade IT infrastructure solutions across Japan.',
      icon: Sparkles,
      stats: '20+ projects delivered'
    },
    {
      year: '2021',
      title: 'Rapid Growth',
      description: 'Expanded our certified engineering team and secured long-term contracts with major enterprise clients.',
      icon: TrendingUp,
      stats: '100+ projects completed'
    },
    {
      year: '2022',
      title: 'Service Expansion',
      description: 'Added AV solutions and IT warehousing to our portfolio, becoming a full-spectrum IT services provider.',
      icon: Code,
      stats: '250+ projects completed'
    },
    {
      year: '2023',
      title: 'Regional Expansion',
      description: 'Extended on-site capabilities to Osaka, Nagoya and surrounding regions to serve more enterprise clients.',
      icon: Globe,
      stats: '400+ projects completed'
    },
    {
      year: '2024',
      title: 'Industry Leader',
      description: 'Recognized as a leading IT infrastructure partner in Japan with 98% client satisfaction.',
      icon: Award,
      stats: '500+ projects completed'
    },
  ];

  const team = [
    {
      name: 'Glenn R.',
      role: 'Chief Executive Officer',
      image: glennceoImg,
      bio: '15+ years in IT infrastructure services, leading enterprise projects across Japan with a focus on delivering reliable, scalable solutions.',
      expertise: ['Strategic Planning', 'Business Development', 'Client Relations'],
      linkedin: '#'
    },
    {
      name: 'Earlwin C.',
      role: 'Head of Technology',
      image: earlitImg,
      bio: 'Seasoned network and infrastructure engineer overseeing technical delivery, solution design, and engineering team mentorship.',
      expertise: ['Network Architecture', 'Solution Design', 'Technical Delivery'],
      linkedin: '#'
    },
    {
      name: 'Lawrence C.',
      role: 'Director of Operations',
      image: lawropsImg,
      bio: 'Operations expert ensuring seamless project execution, resource planning, and exceptional client satisfaction across all engagements.',
      expertise: ['Process Optimization', 'Project Management', 'Quality Assurance'],
      linkedin: '#'
    },
  ];

  const achievements = [
    { year: '2024', award: 'Best IT Infrastructure Services Provider - Japan', organization: 'IT Excellence Awards' },
    { year: '2023', award: 'Top Enterprise Technology Partner', organization: 'Japan Business Technology Forum' },
    { year: '2023', award: 'Top 10 IT Solutions Firms - Japan', organization: 'Business Today' },
    { year: '2022', award: 'Excellence in Network Infrastructure', organization: 'APAC Technology Council' },
  ];

  const whyChooseUs = [
    {
      icon: Target,
      title: 'Deep Technical Expertise',
      description: 'Our certified engineers bring hands-on expertise across networking, AV systems, data centers, and security.',
      stats: '15+ years experience'
    },
    {
      icon: Users,
      title: 'Dedicated Project Teams',
      description: 'Each engagement is handled by a dedicated team with a named project manager and clear escalation paths.',
      stats: 'Dedicated PMs'
    },
    {
      icon: Globe,
      title: 'Multi-Region Coverage',
      description: 'On-site technicians available across Tokyo, Osaka, Nagoya, and surrounding regions.',
      stats: 'Japan-wide coverage'
    },
    {
      icon: Award,
      title: 'Proven Track Record',
      description: '98% client satisfaction with over 500 projects successfully delivered on time and within scope.',
      stats: '500+ projects'
    },
    {
      icon: Zap,
      title: 'Fast Mobilization',
      description: 'We can mobilize on-site engineering teams within days of project approval — no long lead times.',
      stats: 'Rapid deployment'
    },
    {
      icon: CheckCircle,
      title: 'Post-Project Support',
      description: 'Structured handover, full documentation, and optional ongoing maintenance contracts after delivery.',
      stats: 'Ongoing support'
    },
  ];

  // Animate stats on mount
  useEffect(() => {
    stats.forEach((stat, index) => {
      setTimeout(() => {
        let current = 0;
        const increment = stat.value / 50;
        const timer = setInterval(() => {
          current += increment;
          if (current >= stat.value) {
            current = stat.value;
            clearInterval(timer);
          }
          setAnimatedStats(prev => ({
            ...prev,
            [index]: current.toFixed(0)
          }));
        }, 30);
      }, index * 200);
    });
  }, []);

  const TeamModal = ({ member, onClose }) => (
    <AnimatePresence>
      {member && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl max-w-2xl w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full">
              <X className="h-6 w-6" />
            </button>
            
            <div className="flex items-start gap-6 mb-6">
              <img
                src={member.image}
                alt={member.name}
                className="w-24 h-24 rounded-full border-4 border-blue-100"
              />
              <div>
                <h2 className="text-3xl font-bold mb-2">{member.name}</h2>
                <p className="text-blue-600 font-semibold mb-3">{member.role}</p>
                <p className="text-gray-600 leading-relaxed">{member.bio}</p>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-bold mb-3">Areas of Expertise</h3>
              <div className="flex flex-wrap gap-2">
                {member.expertise.map((skill, i) => (
                  <span key={i} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full font-medium">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            
            <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-500">
              <Globe className="mr-2 h-5 w-5" />
              Connect on LinkedIn
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <Helmet>
        <title>About Us - Tasks IT Services | Enterprise IT Infrastructure Solutions</title>
        <meta name="description" content="Learn about Tasks IT Services — our team, values, and commitment to delivering reliable IT infrastructure solutions for enterprises across Japan." />
      </Helmet>

      {/* Enhanced Hero Section with Carousel */}
      <section className="relative h-screen overflow-hidden">
        <Swiper
          modules={[Autoplay, Pagination, Navigation, EffectFade]}
          effect="fade"
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          navigation
          className="h-full"
        >
          {heroSlides.map((slide, index) => (
            <SwiperSlide key={index}>
              <div className="relative h-full">
                <div className="absolute inset-0">
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-purple-900/90 to-cyan-800/85"></div>
                  <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="max-w-4xl text-white text-center mx-auto"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 }}
                      className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20"
                    >
                      <Sparkles className="h-4 w-4 text-yellow-300" />
                      <span className="text-sm font-medium">{slide.highlight}</span>
                    </motion.div>

                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-6xl md:text-7xl font-bold mb-6 leading-tight"
                    >
                      {slide.title}
                    </motion.h1>

                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="text-2xl text-blue-100 mb-8 leading-relaxed"
                    >
                      {slide.subtitle}
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="flex flex-wrap justify-center gap-4"
                    >
                      <Link to="/contact">
                        <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl text-lg px-8 py-6 group">
                          Partner With Us
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      <Link to="/services">
                        <Button size="lg" className="bg-blue-500 text-white border-2 border-white hover:bg-blue-600 shadow-xl text-lg px-8 py-6">
                          Our Services
                        </Button>
                      </Link>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

    
      {/* Our Story Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-3xl blur-3xl opacity-20"></div>
              <img 
                alt="Modern city skyline with skyscrapers" 
                className="relative rounded-2xl shadow-2xl" 
                src="https://images.unsplash.com/photo-1596284492453-162676931bba?q=80&w=2070" 
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full mb-6">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-semibold">Our Journey</span>
              </div>
              <h2 className="text-4xl font-bold mb-6">Our Story</h2>
              <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                Founded in Tokyo in February 2026. Tasks IT Services began with a clear mission: to bridge the gap between the tech industry’s evolving needs and the challenges brought on by global restrictions. As companies struggled with recruitment limitations, we found a way to connect qualified and experienced professionals to industries urgently seeking talent to support their IT operations.
              </p>
              <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                Since then, we’ve grown from a small startup into a steadily progressing tech recruitment agency, placing skilled professionals in roles where they thrive and helping companies build teams that drive innovation.
              </p>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                Our team is composed of former tech professionals and industry specialists who bring real-world insight and genuine passion to every placement. We don’t just fill positions — we build transparent, long-term partnerships grounded in trust and performance.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-md">
                  <div className="text-3xl font-bold text-blue-600 mb-1">2026</div>
                  <div className="text-sm text-gray-600">Year Founded</div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-md">
                  <div className="text-3xl font-bold text-blue-600 mb-1">50+</div>
                  <div className="text-sm text-gray-600">Team Members</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

     
      {/* Core Values Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4">Our Core Values</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              The principles that guide everything we do and define who we are
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -10 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all group cursor-pointer"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r ${value.color} rounded-full mb-4 shadow-lg`}
                >
                  <value.icon className="h-7 w-7 text-white" />
                </motion.div>
                <h3 className="text-2xl font-bold mb-3">{value.title}</h3>
                <p className="text-blue-100 mb-4 leading-relaxed">{value.description}</p>
                <ul className="space-y-2">
                  {value.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center text-sm text-blue-200">
                      <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4">
              Meet Our <span className="text-blue-600">Leadership Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced professionals dedicated to your success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                onClick={() => setSelectedMember(member)}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer group overflow-hidden"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-1 group-hover:text-blue-600 transition-colors">{member.name}</h3>
                  <p className="text-blue-600 font-semibold mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{member.bio}</p>
                  <div className="flex flex-wrap gap-2">
                    {member.expertise.slice(0, 2).map((skill, i) => (
                      <span key={i} className="text-xs px-3 py-1 bg-blue-50 text-blue-700 rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TeamModal member={selectedMember} onClose={() => setSelectedMember(null)} />

      
      

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Target className="h-20 w-20 mx-auto mb-6 text-blue-200" />
            </motion.div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to Partner With Us?
            </h2>
            <p className="text-2xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
              Let's discuss your IT infrastructure needs and build a solution that keeps your business running at full performance.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl text-xl px-10 py-7 group">
                  Contact Us Today
                  <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-2 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" className="bg-blue-500 text-white border-2 border-white hover:bg-blue-600 shadow-2xl text-xl px-10 py-7">
                  Explore Services
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;